/**
 * Help-topic registry — the single source of truth for instructional tooltips
 * that can open Counterintelligence for a deeper explanation.
 *
 * Each entry carries THREE separate pieces of copy, stored independently:
 *   - label       — the short visible title inside the tooltip bubble
 *   - tooltipText — the concise visible explanation (1–2 sentences)
 *   - aiQuestion  — the explicitly authored question sent to Counterintelligence
 *
 * The AI is NEVER asked to interpret tooltip prose to guess the question; the
 * question is authored here next to the visible text. See docs/help-tooltips.md
 * for the authoring rules every new entry must follow.
 *
 * A topic is wired to a control with <HelpTooltip helpTopicId="…">, which reads
 * this registry — no per-tooltip chat handlers anywhere else.
 */

// The existing Counterintelligence brand badge, reused at small size inside the
// tooltip's help footer.
export const CI_HELP_ICON_SRC = '/icons/counter_intel_button_chat_ci_badge.png';

// Page identifier used for analytics + the structured help payload when a topic
// is triggered from the Templates & Counts overview.
export const TEMPLATES_PAGE = 'templates';
export const CATALOG_PAGE = 'catalog';
export const VENDORS_PAGE = 'vendors';
export const SETTINGS_PAGE = 'settings';
export const ADD_ITEMS_PAGE = 'add_items';

// Display names for the "Asked from … help" source label in the tray.
const PAGE_LABELS = {
  [TEMPLATES_PAGE]: 'Templates',
  [CATALOG_PAGE]: 'Catalog',
  [VENDORS_PAGE]: 'Vendors',
  [SETTINGS_PAGE]: 'Settings',
  [ADD_ITEMS_PAGE]: 'Add Items',
};
export const helpSourceLabel = (page) =>
  `Asked from ${PAGE_LABELS[page] || page} help`;

export const HELP_TOPICS = {
  'template.par_moq': {
    id: 'template.par_moq',
    label: 'PAR & MOQ settings',
    tooltipText:
      'Set the target amount to keep on hand and any vendor minimum. Countertop calculates what is needed to reach PAR, then raises the order to the MOQ when required.',
    aiQuestion:
      'Why would I use PAR and MOQ settings, and how do they affect the quantity Countertop recommends ordering?',
    aiTopic: 'par_and_moq',
    iconImg: { src: '/par-levels-icon.png', alt: 'Par and min levels icon' },
  },
  'template.schedule': {
    id: 'template.schedule',
    label: 'Schedule',
    tooltipText:
      'Set when this template is normally counted or ordered. Countertop automatically assigns the dates for each new count or order created from it.',
    aiQuestion:
      'Why would I add a schedule to a template, and how does Countertop use it to set count or order dates?',
    aiTopic: 'template_schedule',
  },
  'template.direct_order': {
    id: 'template.direct_order',
    label: 'Direct to order',
    tooltipText:
      'Enter how much you want to order instead of counting what is currently on hand. PAR and on-hand calculations are skipped.',
    aiQuestion:
      'Why would I use direct-to-order instead of counting what I have, and what calculations does Countertop skip?',
    aiTopic: 'direct_to_order',
  },
  'template.setup': {
    id: 'template.setup',
    label: 'Setup',
    tooltipText:
      'Finish setting up this template before starting a count. Add any missing items, locations, schedule, PARs, or ordering settings — or Skip setup to mark it done and start right away.',
    aiQuestion:
      'Why does this template still need setup, and what must be completed before I can start using it?',
    aiTopic: 'template_setup',
  },
  'template.start_count': {
    id: 'template.start_count',
    label: 'Start count',
    tooltipText:
      'Start a new count using this template’s current items, locations, units, and settings.',
    aiQuestion:
      'Why would I start a new count from this template, and which template settings will be used?',
    aiTopic: 'start_count',
  },
  'template.details_history': {
    id: 'template.details_history',
    label: 'View details & history',
    tooltipText:
      'Review the template setup, edit its settings, and view or resume previous counts created from it.',
    aiQuestion:
      'Why would I open template details and history, and what can I review or change there?',
    aiTopic: 'template_details_history',
  },
  'template.eop_filter': {
    id: 'template.eop_filter',
    label: 'End of Period',
    tooltipText:
      'Show templates used to count inventory on hand for ending inventory, reporting, and cost-of-goods calculations. These counts do not create orders.',
    aiQuestion:
      'Why would I use an End-of-Period inventory count, and how is it used to calculate inventory value, cost of goods sold, and actual food cost?',
    aiTopic: 'end_of_period_inventory',
  },
  'template.ordering_filter': {
    id: 'template.ordering_filter',
    label: 'Ordering',
    tooltipText:
      'Show templates used to calculate or enter order quantities. Ordering templates can compare on-hand counts with PARs or let users enter quantities to order directly.',
    aiQuestion:
      'Why would I use an Ordering template, and how does it turn counts or direct entries into vendor orders and prep needs?',
    aiTopic: 'ordering_template',
  },
  'template.create_eop': {
    id: 'template.create_eop',
    label: 'New End-of-Period Template',
    tooltipText:
      'Create a new template for counting inventory at the end of a reporting period. The completed count values what is on hand and is used to calculate cost of goods sold and actual food cost.',
    aiQuestion:
      'Why would I create an End-of-Period template, and what is an end-of-period inventory count used for?',
    aiTopic: 'create_eop_template',
  },
  'template.create_ordering': {
    id: 'template.create_ordering',
    label: 'New Ordering Template',
    tooltipText:
      'Create a new template for placing recurring orders. Count what is on hand and use PARs to calculate order quantities, or enter the quantities to order directly.',
    aiQuestion:
      'Why would I create an Ordering template, and how does it help me place vendor orders or create prep needs?',
    aiTopic: 'create_ordering_template',
  },

  // ── Catalog / Items page ─────────────────────────────────────────────────
  'catalog.overview': {
    id: 'catalog.overview',
    label: 'Catalog',
    tooltipText:
      'Your Catalog is the store’s master list of inventory items. Items created here can be used in counts, ordering templates, recipes, vendor relationships, and reports.',
    aiQuestion:
      'Why would I maintain a Catalog in Countertop, and how do Catalog items connect to counting, ordering, recipes, vendors, and reporting?',
    aiTopic: 'catalog_overview',
  },
  'catalog.import_file': {
    id: 'catalog.import_file',
    label: 'Import a file',
    tooltipText:
      'Upload an inventory list, vendor order guide, spreadsheet, PDF, image, or copied table. Countertop reads the source and prepares the items for review.',
    aiQuestion:
      'Why would I import an inventory file, what kinds of files can Countertop read, and what happens before the imported items are added to my Catalog?',
    aiTopic: 'catalog_import_file',
  },
  'catalog.build_from_menu': {
    id: 'catalog.build_from_menu',
    label: 'Build from your menu',
    tooltipText:
      'Use a restaurant menu to suggest the ingredients and inventory items the restaurant may need. Review and correct the suggestions before using them.',
    aiQuestion:
      'Why would I build my Catalog from a menu, and how does Countertop turn menu dishes into suggested inventory items?',
    aiTopic: 'catalog_build_from_menu',
  },
  'catalog.manual_entry': {
    id: 'catalog.manual_entry',
    label: 'Type them in',
    tooltipText:
      'Add a small number of inventory items manually when you do not have a file or menu to import.',
    aiQuestion:
      'Why would I enter inventory items manually instead of importing them, and what information should I provide for each item?',
    aiTopic: 'catalog_manual_entry',
  },
  'catalog.ai_import': {
    id: 'catalog.ai_import',
    label: 'Import with AI',
    tooltipText:
      'Countertop interprets the uploaded source, identifies likely items, and proposes units, packages, vendors, and relationships for review.',
    aiQuestion:
      'What does Import with AI do to my inventory file, what information can it suggest, and what should I verify before saving the results?',
    aiTopic: 'catalog_ai_import',
  },
  'catalog.price_trends': {
    id: 'catalog.price_trends',
    label: 'Price trends',
    tooltipText:
      'See how recorded item and vendor prices changed over the selected period. Trends depend on the invoice and price history available in Countertop.',
    aiQuestion:
      'Why would I review price trends, how does Countertop calculate them, and what does it mean when an item has little or no price history?',
    aiTopic: 'catalog_price_trends',
  },
  'catalog.relationship_suggestions': {
    id: 'catalog.relationship_suggestions',
    label: 'Suggestions',
    tooltipText:
      'Suggestions are proposals, separate from confirmed relationships. Accept creates the relationship; Dismiss records that the pair should not be shown again. You can edit the members or relationship type before accepting.',
    aiQuestion:
      'Why would Countertop suggest a relationship between products, and what happens when I approve or reject the suggestion?',
    aiTopic: 'catalog_relationship_suggestions',
  },
  'catalog.inventory_items': {
    id: 'catalog.inventory_items',
    label: 'Catalog view',
    tooltipText:
      'Inventory items are the restaurant-facing products you count, use in recipes, assign to locations, and replenish.',
    aiQuestion:
      'What is an inventory item in Countertop, and how is it different from a vendor product or purchase option?',
    aiTopic: 'catalog_inventory_items',
  },
  'catalog.related_products': {
    id: 'catalog.related_products',
    label: 'Related products',
    tooltipText:
      'Related products groups the different vendor packages and Equivalent members for one countable product. Each card keeps the real package price and shows a comparable standard-unit price when the conversion is known.',
    aiQuestion:
      'What are related products, what do the lettered vendor rows mean, and how are Purchase, Comparable, Lowest price, Count item, and Last used different?',
    aiTopic: 'catalog_related_products',
  },
  'catalog.vendor': {
    id: 'catalog.vendor',
    label: 'Vendor',
    tooltipText:
      'The supplier associated with this product or purchase option. One inventory item may be available from several vendors.',
    aiQuestion:
      'Why can an inventory item have more than one vendor, and how does Countertop keep vendor products separate while counting and ordering?',
    aiTopic: 'catalog_vendor',
  },
  'catalog.pack_size': {
    id: 'catalog.pack_size',
    label: 'Pack/Size',
    tooltipText:
      'Describes how the product is packaged, including the number of inner units and the size of each one.',
    aiQuestion:
      'Why is Pack/Size important, and how does Countertop interpret descriptions such as 4 × 1 gallon, 6 × 2 pounds, or 24 × 12 fluid ounces?',
    aiTopic: 'catalog_pack_size',
  },
  'catalog.unit': {
    id: 'catalog.unit',
    label: 'Unit',
    tooltipText:
      'The unit used to measure or count this item, such as each, pound, ounce, gallon, bottle, bag, or case.',
    aiQuestion:
      'What does Unit mean in the Catalog, and how is a count unit different from a purchase unit or normalized unit?',
    aiTopic: 'catalog_unit',
  },
  'catalog.case_quantity': {
    id: 'catalog.case_quantity',
    label: 'Case Qty',
    tooltipText:
      'The number of countable inner units contained in one full purchase case.',
    aiQuestion:
      'What is Case Quantity, and how is it used when counting full cases, loose inner packages, and calculating price per unit?',
    aiTopic: 'catalog_case_quantity',
  },
  'catalog.split_quantity': {
    id: 'catalog.split_quantity',
    label: 'Split Qty',
    tooltipText:
      'The quantity used when a vendor allows a case to be split into smaller orderable units.',
    aiQuestion:
      'What is Split Quantity, when would a vendor allow a split case, and how does it affect ordering and unit pricing?',
    aiTopic: 'catalog_split_quantity',
  },
  'catalog.case_price': {
    id: 'catalog.case_price',
    label: 'Case $',
    tooltipText:
      'The recorded price for one complete purchase case or outer package.',
    aiQuestion:
      'What is Case Price, and how does Countertop use it with pack size and case quantity to calculate comparable unit costs?',
    aiTopic: 'catalog_case_price',
  },
  'catalog.unit_price': {
    id: 'catalog.unit_price',
    label: 'Unit $',
    tooltipText:
      'The calculated or entered cost for one unit of the product. It depends on the package size and conversion being correct.',
    aiQuestion:
      'What is Unit Price, how is it calculated from the case price, and why can it be unavailable when a package conversion is missing?',
    aiTopic: 'catalog_unit_price',
  },
  'catalog.item_code': {
    id: 'catalog.item_code',
    label: 'Code',
    tooltipText:
      'The vendor or internal product code used to identify this specific item or purchase option.',
    aiQuestion:
      'What is the item Code used for, and how does it help Countertop match invoices, vendor products, and existing inventory items?',
    aiTopic: 'catalog_item_code',
  },
  'catalog.item_history': {
    id: 'catalog.item_history',
    label: 'History',
    tooltipText:
      'View recorded changes to this item, such as prices, packages, vendors, units, or other saved details.',
    aiQuestion:
      'Why would I review an item’s history, and which changes can affect counts, recipe costs, ordering, and past records?',
    aiTopic: 'catalog_item_history',
  },
  'catalog.multivendor_item': {
    id: 'catalog.multivendor_item',
    label: 'Multiple vendors',
    tooltipText:
      'This inventory item has more than one vendor or package option. Expand it to review the products, units, and prices connected to the item.',
    aiQuestion:
      'Why does this inventory item have multiple vendor products, and how does Countertop decide which package to count or order?',
    aiTopic: 'catalog_multivendor_item',
  },

  // ── Vendors page ─────────────────────────────────────────────────────────
  'vendor.overview': {
    id: 'vendor.overview',
    label: 'Vendor Editor',
    tooltipText:
      'Keep one record for each supplier, including its names, contacts, purchasing details, fulfillment method, and order history.',
    aiQuestion:
      'How should I set up and maintain vendors in Countertop, and how do vendor records connect to inventory items, ordering, and purchase history?',
    aiTopic: 'vendor_overview',
  },
  'vendor.completeness': {
    id: 'vendor.completeness',
    label: 'Needs Info',
    tooltipText:
      'A vendor is Complete when its primary contact has an email plus either a contact name or phone number. Needs Info shows vendors that do not yet meet that requirement.',
    aiQuestion:
      'Why is this vendor marked Needs Info, what information is required to make it Complete, and how does that affect purchase orders?',
    aiTopic: 'vendor_completeness',
  },
  'vendor.item_count': {
    id: 'vendor.item_count',
    label: 'Item count',
    tooltipText:
      'Shows how many inventory items are currently linked to this vendor.',
    aiQuestion:
      'What does the item count on a vendor mean, and how are inventory items and vendor products linked to a vendor in Countertop?',
    aiTopic: 'vendor_item_count',
  },
  'vendor.contact_setup': {
    id: 'vendor.contact_setup',
    label: 'Set up contact information',
    tooltipText:
      'Add the contact details Countertop can use when preparing and sending purchase orders to this vendor.',
    aiQuestion:
      'Why does this vendor need contact information, which fields should I complete, and how does Countertop use them when sending orders?',
    aiTopic: 'vendor_contact_setup',
  },
  'vendor.last_order': {
    id: 'vendor.last_order',
    label: 'Last order',
    tooltipText:
      'Shows the most recent purchase order that was actually sent to this vendor, including its date, total, and sender. Draft orders are not included.',
    aiQuestion:
      'How is the vendor’s Last Order determined, and why might it say No orders sent even when draft orders exist?',
    aiTopic: 'vendor_last_order',
  },
  'vendor.order_history': {
    id: 'vendor.order_history',
    label: 'Order history',
    tooltipText:
      'Review purchase orders sent to this vendor, including their status, amount, item count, and sender.',
    aiQuestion:
      'What appears in a vendor’s order history, which orders are included, and how can I use it to review purchasing activity?',
    aiTopic: 'vendor_order_history',
  },

  // ── Store Settings page ──────────────────────────────────────────────────
  'settings.overview': {
    id: 'settings.overview',
    label: 'Settings',
    tooltipText:
      'One place to manage this store’s identity, billing, team, reporting categories, and your account.',
    aiQuestion:
      'What can I manage on the Settings page, and which settings affect counts, ordering, reporting, and billing?',
    aiTopic: 'settings_overview',
  },
  'settings.store_details': {
    id: 'settings.store_details',
    label: 'Store Details',
    tooltipText:
      'This store’s basic identity — name, address, website or menu link, phone, email, timezone, and currency.',
    aiQuestion:
      'Why would I complete my store details, and where does Countertop use the store name, address, website, phone, email, timezone, and currency?',
    aiTopic: 'settings_store_details',
  },
  'settings.subscription': {
    id: 'settings.subscription',
    label: 'Subscription',
    tooltipText:
      'Your plan and billing status. Each paid subscription covers one store with unlimited team members; adding another store needs its own subscription.',
    aiQuestion:
      'How does Countertop billing work per store, what does my subscription include, and what happens when I add another store?',
    aiTopic: 'settings_subscription',
  },
  'settings.team': {
    id: 'settings.team',
    label: 'Team',
    tooltipText:
      'Invite people to work in this store by email, assign roles and permissions, or import staff from Clover.',
    aiQuestion:
      'Why would I invite teammates to my store, how do roles and permissions work, and what does an invitation send them?',
    aiTopic: 'settings_team',
  },
  'settings.pnl_categories': {
    id: 'settings.pnl_categories',
    label: 'P&L Categories',
    tooltipText:
      'Group inventory categories into P&L reporting buckets (Food, Liquor, Wine, etc.) used by the End-of-Period COGS report.',
    aiQuestion:
      'Why would I map inventory categories to P&L categories, and how does that affect my cost-of-goods and inventory reports?',
    aiTopic: 'settings_pnl_categories',
  },
  'settings.cost_budgets': {
    id: 'settings.cost_budgets',
    label: 'Cost Budgets',
    tooltipText:
      'Set the target cost for each P&L category — what its cost of goods should be as a percentage of that category\'s own sales. Countertop can suggest a starting point from the kind of restaurant it read from your menu.',
    aiQuestion:
      'What is a cost budget for a P&L category, how should I choose a target percentage, and where does Countertop\'s suggested default come from?',
    aiTopic: 'settings_cost_budgets',
  },
  'settings.account': {
    id: 'settings.account',
    label: 'Account',
    tooltipText:
      'Your sign-in account. This is where you permanently delete your Countertop account and store memberships.',
    aiQuestion:
      'What happens when I delete my Countertop account, and what should I do with my stores and team first?',
    aiTopic: 'settings_account',
  },
  'settings.store_name': {
    id: 'settings.store_name',
    label: 'Store Name',
    tooltipText:
      'The display name for this store, shown across counts, orders, reports, and emails.',
    aiQuestion:
      'What does Countertop use my store name for, and does changing it affect counts, orders, or reports?',
    aiTopic: 'settings_store_name',
  },
  'settings.store_address': {
    id: 'settings.store_address',
    label: 'Address',
    tooltipText:
      'The store’s street address, kept with the store record. Optional.',
    aiQuestion:
      'What is the store address used for in Countertop, and is it required?',
    aiTopic: 'settings_store_address',
  },
  'settings.store_website': {
    id: 'settings.store_website',
    label: 'Website / menu link',
    tooltipText:
      'Your website or online menu link. Countertop uses it to find your menu and suggest starter inventory items.',
    aiQuestion:
      'Why would I add my website or menu link, and how does Countertop use it to build starter inventory items?',
    aiTopic: 'settings_store_website',
  },
  'settings.store_phone': {
    id: 'settings.store_phone',
    label: 'Phone',
    tooltipText:
      'The store’s main phone number, kept with the store record.',
    aiQuestion:
      'What is the store phone number used for in Countertop?',
    aiTopic: 'settings_store_phone',
  },
  'settings.store_email': {
    id: 'settings.store_email',
    label: 'Email',
    tooltipText:
      'The store’s main contact email address.',
    aiQuestion:
      'What is the store email address used for in Countertop, and is it different from my sign-in email?',
    aiTopic: 'settings_store_email',
  },
  'settings.store_timezone': {
    id: 'settings.store_timezone',
    label: 'Timezone',
    tooltipText:
      'The store’s local time zone, used when dating counts, orders, and scheduled runs.',
    aiQuestion:
      'Why does my store need a time zone, and how does it affect count dates, order dates, and schedules?',
    aiTopic: 'settings_store_timezone',
  },
  'settings.store_currency': {
    id: 'settings.store_currency',
    label: 'Currency',
    tooltipText:
      'The currency used for prices, order totals, and inventory valuation for this store.',
    aiQuestion:
      'What does the store currency setting control, and when should I change it from USD?',
    aiTopic: 'settings_store_currency',
  },

  // ── Add Items page (onboarding final step) ───────────────────────────────
  'additems.final_step': {
    id: 'additems.final_step',
    label: 'Add your items',
    tooltipText:
      'The last setup step: get your inventory items in so you can count, order, and track costs. Start rough — you can add, edit, or import more anytime.',
    aiQuestion:
      'Why do I need to add items before counting or ordering, what happens after I add them, and what can I finish later?',
    aiTopic: 'additems_final_step',
  },
  'additems.sample_items': {
    id: 'additems.sample_items',
    label: 'Try sample items',
    tooltipText:
      'Load five demo items — a mini bar-and-kitchen inventory — to try counting and ordering. They are flagged as samples, and you can delete or replace them with your real items anytime.',
    aiQuestion:
      'What are sample items for, will they affect my real inventory or reports, and how do I remove them later?',
    aiTopic: 'additems_sample_items',
  },
  'additems.setup_email': {
    id: 'additems.setup_email',
    label: 'Send setup email',
    tooltipText:
      'Get an email you can reply to later with an invoice, spreadsheet, menu, order guide, photo, or pasted list. Countertop reads your reply and prepares the items for review.',
    aiQuestion:
      'How does the setup email import work, what can I reply with, and what happens after Countertop reads my reply?',
    aiTopic: 'additems_setup_email',
  },
  'additems.attach_file': {
    id: 'additems.attach_file',
    label: 'Attach file',
    tooltipText:
      'Drop a file, paste a copied table or list, or browse — order guides, Excel, CSV, text, PDF, or images, including a photo of a paper list.',
    aiQuestion:
      'What files can I attach for import, and what should the file contain for the best results?',
    aiTopic: 'additems_attach_file',
  },
  'additems.review_save': {
    id: 'additems.review_save',
    label: 'Review & save',
    tooltipText:
      'Check the items Countertop found, fix anything inline, and approve. Nothing is added to your catalog until you approve.',
    aiQuestion:
      'What should I check at the Review and save step, and can I edit or reject items before they are added?',
    aiTopic: 'additems_review_save',
  },
  'additems.your_items': {
    id: 'additems.your_items',
    label: 'Your items',
    tooltipText:
      'Everything you add — from any method — lands here for review. Edit inline, then approve to build your count and order templates.',
    aiQuestion:
      'What should I review in the Your items list before approving, and what does building my counts create?',
    aiTopic: 'additems_your_items',
  },
  'additems.approve_build': {
    id: 'additems.approve_build',
    label: 'Approve & build my counts',
    tooltipText:
      'Creates count and order templates from your approved items and opens your first counts. You can edit the templates and items afterward.',
    aiQuestion:
      'What does Approve and build my counts create, and can I change the templates or items afterward?',
    aiTopic: 'additems_approve_build',
  },
  'additems.skip': {
    id: 'additems.skip',
    label: 'Skip for now',
    tooltipText:
      'Adding items is optional — you can add, edit, or import items anytime from the Items page.',
    aiQuestion:
      'What happens if I skip adding items for now, and how do I add items later?',
    aiTopic: 'additems_skip',
  },

  // ── Import review workflow (shared approval table) ───────────────────────
  'import.review_actions': {
    id: 'import.review_actions',
    label: 'Review actions',
    tooltipText:
      'Each row’s action decides how it lands: New creates the item, Existing leaves it unchanged, Price change updates its recorded price, and Disapproved skips it.',
    aiQuestion:
      'What do New, Existing, Price change, and Disapproved mean when reviewing imported items, and how do I choose the right action for each row?',
    aiTopic: 'import_review_actions',
  },
  'import.how_it_lands': {
    id: 'import.how_it_lands',
    label: 'How it will land',
    tooltipText:
      'A plain-English preview of what approving does for this row — the item it creates or updates, its count unit, and its first vendor product.',
    aiQuestion:
      'How does Countertop describe what an import row will do, and what should I verify in the How it will land preview?',
    aiTopic: 'import_how_it_lands',
  },
  'import.add_to_counts': {
    id: 'import.add_to_counts',
    label: 'Add to Counts',
    tooltipText:
      'Add the imported item to an in-progress count or template as it lands. Shown only when a count is currently in progress.',
    aiQuestion:
      'What does adding an imported item to an in-progress count do, and when would I use it?',
    aiTopic: 'import_add_to_counts',
  },

  // ── Start count/order choice modal (web now · later · mobile) ────────────
  'countstart.web_tool': {
    id: 'countstart.web_tool',
    label: 'Open in the web app',
    tooltipText:
      'Start right now on this computer — you’re taken straight to the count or order screen. Great at a desk; for walking the shelves, the mobile app is usually easier.',
    aiQuestion:
      'How do I start a count or order in the web app instead of on my phone, and what happens when I open the tool on my computer?',
    aiTopic: 'countstart_web_tool',
  },
  'countstart.start_later': {
    id: 'countstart.start_later',
    label: 'Start now, finish later',
    tooltipText:
      'Create the session now and finish it whenever you’re ready — it waits in your in-progress list on any device, phone or computer.',
    aiQuestion:
      'What does Start Now create, where do I find the session afterward, and can I finish it on my phone or computer?',
    aiTopic: 'countstart_start_later',
  },
  'countstart.mobile': {
    id: 'countstart.mobile',
    label: 'Use Countertop-App Mobile',
    tooltipText:
      'Create the session and run it on your phone with the mobile keypad — the best way to count or order while walking the shelves. We’ll give you instructions to get started.',
    aiQuestion:
      'Why is counting or ordering better on the mobile app, and how do I get the session onto my phone?',
    aiTopic: 'countstart_mobile',
  },
};

export const getHelpTopic = (id) => HELP_TOPICS[id] || null;

/**
 * The question sent to Counterintelligence for a topic. Every topic should
 * author its own aiQuestion; this deterministic fallback (never an AI
 * interpretation of tooltip prose) keeps future entries safe.
 */
export const aiQuestionForTopic = (def) =>
  def.aiQuestion || `Why would I use ${def.label}, and how does it work in Countertop?`;

// Context keys a tooltip site may pass that are safe + useful to forward to
// Counterintelligence. Anything not listed here (item lists, counts, history,
// invoice detail) is deliberately dropped.
const SAFE_CONTEXT_KEYS = [
  'missing_setup_steps',
  'par_enabled',
  'moq_exists',
  'direct_order_enabled',
  'schedule',
  'item_count',
  'prepared_item_count',
  'vendor_item_count',
  'template_status',
  'selected_item_id',
  'selected_vendor_product_id',
  'active_catalog_tab',
  'import_method',
  'vendor_count',
  'vendor_id',
  'vendor_status',
  'vendor_item_count',
];

const pickSafeContext = (context = {}) => {
  const out = {};
  SAFE_CONTEXT_KEYS.forEach((k) => {
    if (context[k] !== undefined && context[k] !== null) out[k] = context[k];
  });
  return out;
};

const currentStoreId = () => {
  try { return localStorage.getItem('selectedStoreId') || null; } catch (_) { return null; }
};

/**
 * Analytics for help usage. Fire-and-forget, guarded — analytics must never
 * break the UI, and only metadata (never conversation content) is sent.
 */
export const trackHelpEvent = (name, props = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, props);
    }
  } catch (_) { /* analytics must never block help */ }
};

/**
 * Ask Counterintelligence for the deeper explanation behind a help topic.
 * Opens the existing tray and submits the topic's authored question with a
 * structured payload — the raw tooltip text travels only as supporting context.
 */
export const requestHelpFromCounterIntelligence = ({ topicId, page = TEMPLATES_PAGE, section, context = {} } = {}) => {
  const def = getHelpTopic(topicId);
  if (!def || typeof window === 'undefined') return null;
  const question = aiQuestionForTopic(def);
  const payload = {
    source: 'ui_help_tooltip',
    help_topic_id: def.id,
    page,
    section: section || undefined,
    question,
    tooltip_text: def.tooltipText,
    ai_topic: def.aiTopic,
    store_id: context.store_id !== undefined ? context.store_id : currentStoreId(),
    template_id: context.template_id != null ? context.template_id : null,
    template_type: context.template_type || undefined,
    ...pickSafeContext(context),
  };
  trackHelpEvent('help_tooltip_ai_selected', {
    help_topic_id: def.id,
    page,
    template_type: payload.template_type,
  });
  window.dispatchEvent(new CustomEvent('counter-intelligence:ask', {
    detail: {
      text: question,
      source: payload.source,
      helpTopicId: def.id,
      sourceLabel: helpSourceLabel(page),
      helpContext: payload,
    },
  }));
  return payload;
};
